import React, { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, X } from "lucide-react";
import { downloadChangeLogCsv } from "@/lib/noteClaim";

const INK = "#141412", SUB = "#6C6C69", FAINT = "#9B9B97", HAIR = "#E9E9EC";
const LABELS = { note_edit: "Note edited", recode_started: "Recode started", recode_finished: "Recode finished", recode_from_note: "Codes changed by recode", sign: "Visit signed", unsign: "Visit unsigned" };
const fmtWhen = (iso) => { const d = new Date(iso || ""); return isNaN(d) ? "" : `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`; };
const diff = (a = [], b = []) => ({ add: b.filter((x) => !a.includes(x)), del: a.filter((x) => !b.includes(x)) });

// Code changes as the server logged them: changes[] lines, else icd/cpt before→after.
function deltas(e) {
  if (Array.isArray(e.changes) && e.changes.length) return e.changes.map((c) => `${c.label || c.field}${c.before != null || c.after != null ? `: ${c.before ?? "—"} → ${c.after ?? "—"}` : ""}`);
  const dt = e.detail || {}; const out = [];
  [["ICD", dt.icd_before, dt.icd_after], ["CPT", dt.cpt_before, dt.cpt_after]].forEach(([k, a, b]) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return;
    const { add, del } = diff(a, b);
    if (add.length) out.push(`${k} added: ${add.join(", ")}`);
    if (del.length) out.push(`${k} removed: ${del.join(", ")}`);
  });
  if (dt.source) out.push(`Source: ${dt.source === "custom_note" ? "edited note" : dt.source === "standard_soap" ? "Standard SOAP" : dt.source}`);
  if (dt.error || dt.recode_error) out.push(`Error: ${dt.error || dt.recode_error}`);
  return out;
}

const CAT = { note: "#1F9D55", claim: "#B4791A", coding: "#B4791A", sign: "#4A4AC2", unsign: "#4A4AC2", patient: "#4A4AC2" };

// Unified diff the server logged: array of { t: hunk|ctx|del|add, s }.
function DiffView({ diff }) {
  if (!Array.isArray(diff) || !diff.length) return null;
  const sty = { hunk: { color: FAINT }, ctx: { color: SUB }, del: { color: "#9a1f1f", background: "#FDECEC" }, add: { color: "#13683a", background: "#E7F6EE" } };
  return (
    <pre className="mt-1 rounded-[8px] overflow-x-auto text-[11.5px] leading-[1.5]" style={{ border: `1px solid ${HAIR}`, background: "#FBFBFA", padding: "6px 8px", fontFamily: "ui-monospace,Menlo,Consolas,monospace", whiteSpace: "pre-wrap" }} data-testid="er-log-diff">
      {diff.map((l, i) => <div key={i} style={{ ...(sty[l.t] || {}) }}>{l.t === "add" ? "+ " : l.t === "del" ? "- " : l.t === "hunk" ? "" : "  "}{l.s}</div>)}
    </pre>
  );
}

export function NoteHistory({ entries, noteId, visitId, onClose }) {
  const [busy, setBusy] = useState(false);
  const csv = async () => { setBusy(true); try { await downloadChangeLogCsv(noteId, visitId); } catch { toast.error("Couldn't download the history."); } finally { setBusy(false); } };
  return (
    <div className="mb-3 rounded-[10px] bg-white" style={{ border: `1px solid ${HAIR}` }} data-testid={`er-log-${noteId}`}>
      <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: `1px solid ${HAIR}` }}>
        <span className="text-[13px] font-bold" style={{ color: INK }}>Note history</span>
        <span className="text-[11.5px]" style={{ color: FAINT }}>Permanent record · can't be edited or deleted</span>
        <span className="flex-1" />
        <button onClick={csv} disabled={busy} className="er-abtn" data-testid={`er-log-csv-${noteId}`}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}Download CSV</button>
        <button onClick={onClose} aria-label="Close note history" className="er-abtn" style={{ padding: "0 7px" }} data-testid={`er-log-close-${noteId}`}><X size={13} /></button>
      </div>
      <div className="px-3 py-1 max-h-[340px] overflow-y-auto">
        {entries.length ? entries.map((e, i) => {
          const lines = deltas(e);
          return (
            <div key={e.id || i} className="py-2 text-[12px]" style={{ borderTop: i ? "1px solid #F4F4F2" : "none" }} data-testid={`er-log-entry-${i}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-semibold" style={{ color: CAT[e.category] || INK }}>{e.label || LABELS[e.action] || e.action}</span>
                {e.field_label ? <span className="text-[10.5px] font-semibold rounded px-1.5 py-[1px]" style={{ background: "#F0F0F2", color: SUB }} data-testid={`er-log-fieldlabel-${i}`}>{e.field_label}</span> : null}
                {(e.lines_added || e.lines_removed) ? <span className="text-[10.5px] font-semibold tabular-nums">{e.lines_added ? <span style={{ color: "#13683a" }}>+{e.lines_added}</span> : null}{(e.lines_added && e.lines_removed) ? " " : ""}{e.lines_removed ? <span style={{ color: "#9a1f1f" }}>−{e.lines_removed}</span> : null}</span> : null}
                <span className="flex-1" />
                <span className="tabular-nums" style={{ color: FAINT }}>{fmtWhen(e.at || e.timestamp)}</span>
              </div>
              <div style={{ color: SUB }}>{[e.user_name || e.user_email || "—", e.ip ? `IP ${e.ip}` : ""].filter(Boolean).join(" · ")}</div>
              {(e.user_id || e.user_agent) ? <div className="text-[10.5px] truncate" style={{ color: FAINT }} title={e.user_agent || ""} data-testid={`er-log-agent-${i}`}>{[e.user_id ? `User ID ${e.user_id}` : "", e.user_agent || ""].filter(Boolean).join(" · ")}</div> : null}
              {lines.length ? <ul className="mt-0.5 list-disc pl-4" style={{ color: INK }}>{lines.map((l, k) => <li key={k}>{l}</li>)}</ul> : null}
              <DiffView diff={e.diff} />
            </div>
          );
        }) : <div className="py-2 text-[12px]" style={{ color: FAINT }}>No history yet.</div>}
      </div>
    </div>
  );
}
